<template>
  <div class="flex xl:inline-flex xl:items-center flex-col justify-between xl:flex-row  gap-8 bg-white rounded-lg shadow px-4 py-3 relative">
    <div v-if="focus_places" class="w-[40%] shrink-0 h-9 min-w-[200px]"></div>
    <div v-if="focus_places" class="fixed w-full h-full top-0 left-0 z-0" @click="blurPlaces"></div>
    <div :class="{
      'absolute -top-3 z-10 -left-4 bg-white rounded-lg px-4 py-3 shadow w-[40%] shrink-0 min-w-[420px] max-h-[80vh] overflow-y-auto': focus_places,
      'w-[40%] shrink-0 min-w-[200px]': !focus_places
    }">
      <div :class="{
        'border-2 border-mk-dark-blue border-solid rounded-md px-4 py-3 relative -top-[2px] -left-[2px]': focus_places
      }">
        <div class="text-sm text-gray-700 font-semibold">{{ i18n.t('marketplace.general.where') }}</div>
        <input v-if="!profile" type="text" v-model="location_name" placeholder="Search destinations or places..." @input="getLocationSearch" @focus.stop="focusPlaces()" class="border-0 w-full text-sm focus:ring-0 focus:border-0 focus:outline-none">
        <div v-else class="w-full text-sm">{{ profile.name }}</div>
      </div>
      <template v-if="focus_places">
        <div v-if="regions.length" class="mt-2">
          <div class="text-sm font-semibold mb-1 text-gray-700">{{ i18n.t('marketplace.welcome.destinations') }}</div>
          <div v-for="region in regions" :key="region.id" class="cursor-pointer text-sm font-medium py-2 px-2 rounded hover:bg-gray-50 flex items-center gap-2" @click.stop="setLocation(region)">
            <img v-if="region.flag_url" :src="region.flag_url" :alt="region.name" class="w-6 h-6 rounded object-cover" />
            <div class="flex flex-col">
              <span>{{ region.name }}</span>
              <span v-if="region.country" class="text-xs text-gray-500">{{ region.country }}</span>
            </div>
          </div>
        </div>
        <div v-if="cities.length" class="mt-2">
          <div class="text-sm font-semibold mb-1 text-gray-700">{{ i18n.t('marketplace.welcome.places') }}</div>
          <div v-for="city in cities" :key="city.id" class="cursor-pointer text-sm font-medium py-2 px-2 rounded hover:bg-gray-50 flex items-center gap-2" @click="setLocation(city)">
            <img v-if="city.flag_url" :src="city.flag_url" :alt="city.name" class="w-6 h-6 rounded object-cover" />
            <div class="flex flex-col">
              <span>{{ city.name }}</span>
              <span v-if="city.region" class="text-xs text-gray-500">{{ city.region }}<template v-if="city.country">, {{ city.country }}</template></span>
              <span v-else-if="city.country" class="text-xs text-gray-500">{{ city.country }}</span>
            </div>
          </div>
        </div>
        <div v-if="products.length" class="mt-2">
          <div class="text-sm font-semibold mb-1 text-gray-700">{{ i18n.t('marketplace.welcome.adventures') }}</div>
          <div class=" gap-2">
            <a v-for="product in products" :key="product.id" :href="`/marketplace/items/${product.slug}`" class="cursor-pointer flex w-full items-center gap-2 rounded hover:bg-gray-50 text-gray-800 hover:text-gray-900 no-underline group mb-2">
              <div v-if="product.image_url" class=" max-w-8 max-h-8 rounded overflow-hidden mb-1">
                <img :src="product.image_url" :alt="product.name" class="w-full h-full object-cover group-hover:scale-105 transition-transform" />
              </div>
              <div class="px-1">
                <div class="text-xs font-medium line-clamp-2">{{ product.name }}</div>
                <div class="text-xs text-gray-500">{{ product.location }}</div>
              </div>
            </a>
          </div>
        </div>
        <div v-if="loading_places" class="mt-2 text-sm text-center text-gray-500">{{ i18n.t('marketplace.general.loading') }}</div>
        <div v-if="!regions.length && !cities.length && !loading_places && location_name.length >= 2" class="mt-2 text-sm h-32 flex flex-col items-center justify-center">
          <div class="font-semibold">{{ i18n.t('marketplace.general.no__results') }}</div>
          <div class="text-gray-700">{{ i18n.t('marketplace.general.try_different_search') }}</div>
        </div>
      </template>
    </div>
    <DatePicker ref="datepicker" class="w-[40%]" v-model="dates" is-range is24hr :minute-increment="60" mode="date" :min-date="new Date()">
      <template #default="{ inputValue, inputEvents }">
        <div class="text-sm text-gray-700 font-semibold">{{ i18n.t('marketplace.general.when') }}</div>
        <div class="flex items-center gap-1">
          <input class="border-0 text-sm focus:ring-0 focus:border-0 focus:outline-none w-[75px]" :placeholder="i18n.t('marketplace.welcome.anytime')" :value="inputValue.start" v-on="inputEvents.start" />
          <span v-if="dates.end" class="inline-block h-5 leading-5">-</span>
          <input class="border-0 text-sm focus:ring-0 focus:border-0 focus:outline-none w-[75px]"  :value="inputValue.end" v-on="inputEvents.end" />
        </div>
      </template>
    </DatePicker>
    <button class="jt-btn jt-mk-btn" :disabled="!valid" @click="search">{{ i18n.t('marketplace.general.search') }}</button>
  </div>
</template>

<script>
import i18n from '../../../packs/i18n_js/index.js';
import axios from '../../shared/custom_axios.js';
import DatePicker from 'v-calendar/lib/components/date-picker.umd';

export default {
  name: 'SearchDestination',
  components: { DatePicker },
  props: {
    emitChanges: {
      type: Boolean,
      default: false
    },
    profile: {
      type: Object,
      required: false,
      default: null
    }
  },
  data() {
    return {
      focus_places: false,
      loading_places: false,
      cities: [],
      regions: [],
      products: [],
      location_name: '',
      location_id: null,
      dates: { start: null, end: null },
    };
  },
  computed: {
    valid() {
      return this.location_id ;
    }
  },
  created() {
    // Initialize with default values if needed
    const params = new URLSearchParams(window.location.search);
    this.location_name = params.get('location_name') || '';
    this.location_id = params.get('location_id') || null;
    if (params.get('start_date')) {
      this.dates.start = new Date(params.get('start_date'));
    }
    if (params.get('end_date')) {
      this.dates.end = new Date(params.get('end_date'));
    }
  },
  methods: {
    focusPlaces() {
      this.focus_places = true;
      this.getLocationSearch();
    },
    blurPlaces() {
      this.focus_places = false;
    },
    getLocationSearch() {
      this.$emit('search-location', this.location_name);
    },
    setLocation(location) {
      this.$emit('set-location', location);
      this.location_name = location.name;
      this.location_id = location.id;
      this.blurPlaces();

      if (this.valid) {
        this.search();
      }
    },
    getLocationSearch() {
      this.loading_places = true;
      axios.get('/marketplace/location_search', {
          params: {
            query: this.location_name
          }
        })
        .then(response => {
          this.cities = response.data.cities || [];
          this.regions = response.data.regions || [];
          this.products = response.data.products || [];
        })
        .catch(error => {
          console.error('Error fetching location search:', error);
        })
        .finally(() => {
          this.loading_places = false;
        });
    },
    search() {
      if (this.emitChanges) {
        this.$emit('change', 
          {
            name: this.location_name,
            id: this.location_id
          },
          this.dates
        );
      } else {
        window.location.href =`/marketplace/items?location_id=${this.location_id}&location_name=${encodeURIComponent(this.location_name)}&start_date=${this.dates.start ? this.dates.start.toISOString().split('T')[0] : ''}&end_date=${this.dates.end ? this.dates.end.toISOString().split('T')[0] : ''}`;
      }
    }
  }
};
</script>